/**
 * Avatar utility functions and constants
 */

/**
 * Color palette for avatars - 24 distinct dark/saturated colors
 * Optimized for visibility as text color on 15% opacity backgrounds
 * Colors are grouped by hue family but distributed for maximum distinction
 */
export const AVATAR_COLORS = [
  // Deep Blues & Purples
  '#1A237E', // Indigo
  '#283593', // Deep Indigo
  '#4A148C', // Dark Violet
  '#5B2C6F', // Deep Purple
  '#6A1B9A', // Purple
  '#7B1FA2', // Medium Purple

  // Rich Reds & Pinks
  '#B71C1C', // Dark Red
  '#C2185B', // Deep Pink
  '#AD1457', // Dark Pink
  '#880E4F', // Dark Magenta

  // Deep Greens & Teals
  '#1B5E20', // Forest Green
  '#2E7D32', // Green
  '#004D40', // Dark Teal
  '#00695C', // Teal
  '#006064', // Deep Cyan

  // Deep Blues
  '#01579B', // Dark Blue
  '#0D47A1', // Royal Blue
  '#1565C0', // Medium Blue

  // Warm Colors
  '#E65100', // Deep Orange
  '#BF360C', // Burnt Orange
  '#D84315', // Dark Orange
  '#FF6F00', // Amber
  '#F57C00', // Orange
  '#FF8F00', // Dark Amber
];

/**
 * Generate a deterministic color based on an ID
 * Same ID will always produce the same color
 * Uses FNV-1a hash algorithm for better distribution with UUIDs
 * @param id - Unique identifier
 * @returns Hex color string from the palette
 */
export const getAvatarColor = (id: string): string => {
  // Remove dashes and convert to lowercase for consistency
  const cleanId = id.replace(/-/g, '').toLowerCase();

  // FNV-1a hash for better distribution
  let hash = 0x811c9dc5; // FNV offset basis (32-bit)

  for (let i = 0; i < cleanId.length; i++) {
    hash ^= cleanId.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193); // FNV prime (32-bit)
  }

  // Additional mixing for better UUID distribution
  // This helps when UUIDs have similar patterns
  hash = ((hash << 13) | (hash >>> 19)) >>> 0;
  hash = Math.imul(hash, 0x5bd1e995);
  hash = ((hash << 15) | (hash >>> 17)) >>> 0;

  // Ensure positive index
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

/**
 * Extract initials from first and last names
 * @param firstName - Patient's first name
 * @param lastName - Patient's last name
 * @returns Uppercase initials or "?" if no names provided
 */
export const getInitials = (firstName?: string, lastName?: string): string => {
  const first = firstName?.trim()?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.trim()?.charAt(0)?.toUpperCase() || '';
  return `${first}${last}` || '?';
};

/**
 * Convert hex color to RGBA with specified opacity
 * @param hex - Hex color string (with or without #)
 * @param opacity - Opacity value between 0 and 1
 * @returns RGBA color string
 */
export const hexToRgba = (hex: string, opacity: number): string => {
  // Remove # if present
  const cleanHex = hex.replace('#', '');

  // Parse hex values
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};